// Reports a first-experience test's exposure (firstExperienceTests.ts): the first time the test changes (or
// could change) what the person sees, once per signed-out device and once per account. On an account's first
// exposure the side is also written to the account (`experiment_variants`), and again at a later exposure moment
// while the account does not hold it yet, so every device it signs in on shows and reports the same side. Forced QA
// sides are never reported or written.

import type { UserData } from "../constants/GlobalTypesAndEnumsAndConstants";
import { identifyUserForAnalytics, trackEvent } from "../backend/utils/helper-methods";
import { updateUser } from "../backend/models/UserModel";
import {
  FIRST_EXPERIENCE_TEST_ASSIGNED_EVENT,
  FirstExperienceTestAssignment,
  getFirstExperienceTestExposureEventProperties,
  recordFirstExperienceTestFirstExposure,
} from "./firstExperienceTests";

export function trackFirstExperienceTestExposure(
  assignment: FirstExperienceTestAssignment | null | undefined,
  user: UserData | null | undefined,
  {
    // AuthContext passes false: it identifies on every profile snapshot itself, with fresh data
    identifyAfterwards = true,
    // AuthContext passes false: it writes T5b's side in the same write as the country it was drawn for
    writeSideToAccount = true,
  }: { identifyAfterwards?: boolean; writeSideToAccount?: boolean } = {}
): void {
  if (!assignment) return;
  const isFirstExposure = recordFirstExperienceTestFirstExposure(assignment);
  // Written until the account holds the side, not only on the first exposure: a write that never reached the server
  // (the tab closed while offline; updateUser does not say) is made again at the next exposure moment, which the
  // record on this device would otherwise stop. Once the account's profile has the side it is "held_by_account".
  const isTheSideStillToWriteToTheAccount =
    writeSideToAccount &&
    Boolean(assignment.accountId) &&
    !assignment.isForcedForQa &&
    assignment.sideSource !== "held_by_account";
  if (isTheSideStillToWriteToTheAccount) {
    // Only this key (updateUser merges maps). Not a transaction: should two devices expose the same account
    // for the first time at the same moment, the later write is the side the account keeps.
    void updateUser(assignment.accountId, { experiment_variants: { [assignment.experimentId]: assignment.variant } });
  }
  if (!isFirstExposure) return;
  // Exactly the experiment, the side and the bucket; deck context is opted out (explicit null)
  void trackEvent(
    FIRST_EXPERIENCE_TEST_ASSIGNED_EVENT,
    getFirstExperienceTestExposureEventProperties(assignment),
    user ?? null,
    null
  );
  // The person record carries the side (identifyUserForAnalytics reads the account's sides and this device's
  // records), so payments and later answers, which land on the account, join to it.
  if (identifyAfterwards && user?.id) void identifyUserForAnalytics(user);
}
